import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Typography, Collapse, Tag, Flex, Button, Modal, Form, App, Empty, Tooltip, Switch, Checkbox, theme } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, LaptopOutlined } from "@ant-design/icons";
import { useMemo, useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { useQ_PageReadiness_Profiles, type PageReadiness_Profiles_QueryData } from "@/hooks/Readiness/useQ_PageReadiness_Profiles";
import { useM_PageReadiness_ProfileCreate } from "@/hooks/Readiness/useM_PageReadiness_ProfileCreate";
import { useM_PageReadiness_ProfileUpdate } from "@/hooks/Readiness/useM_PageReadiness_ProfileUpdate";
import { useM_PageReadiness_ProfileDelete } from "@/hooks/Readiness/useM_PageReadiness_ProfileDelete";
import { useQ_PageReadiness_ProfileResults, type ProfileResult } from "@/hooks/Readiness/useQ_PageReadiness_ProfileResults";
import { useM_PageReadiness_TestRun } from "@/hooks/Readiness/useM_PageReadiness_TestRun";
import { useQ_PageSettings_ProviderConfig } from "@/hooks/OrgSettings/useQ_PageSettings_ProviderConfig";
import {
  App_SessionComposer,
  flattenToFormValues,
  type SessionComposerValues,
} from "@/components/App_SessionComposer/App_SessionComposer";
import { supabase } from "@/configs/supabase/config";

dayjs.extend(relativeTime);
dayjs.extend(duration);

type ProfileRow = PageReadiness_Profiles_QueryData[number];

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  not_run: { color: "default", label: "Not Run" },
  running: { color: "processing", label: "Running" },
  passed: { color: "success", label: "Passed" },
  failed: { color: "error", label: "Failed" },
};

const profileToComposerValues = (profile: ProfileRow): Pick<SessionComposerValues, "customerData" | "quoteDetails"> => ({
  customerData: {
    firstName: profile.first_name ?? "",
    lastName: profile.last_name ?? "",
    dob: profile.date_of_birth ?? "",
    phone: profile.phone ?? "",
    email: profile.email ?? "",
    driversLicense: profile.drivers_license ?? "",
    licenseState: profile.license_state ?? undefined,
    address: {
      street: profile.address_street ?? "",
      city: profile.address_city ?? "",
      state: profile.address_state ?? "",
      zip: profile.address_zip ?? "",
    },
    gender: profile.gender ?? undefined,
    maritalStatus: profile.marital_status ?? undefined,
  },
  quoteDetails: {
    state: profile.rate_state ?? "",
    policyType: profile.policy_type ?? "",
  },
});

export const Route = createFileRoute("/_protected/$organizationId/$flowId/sessions/readiness")({
  component: Page_Readiness,
});

function Page_Readiness() {
  const { token } = theme.useToken();
  const { message, modal } = App.useApp();
  const { organizationId, flowId } = Route.useParams();
  const [flowDbId, setFlowDbId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editProfile, setEditProfile] = useState<ProfileRow | null>(null);
  const [launchingIds, setLaunchingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const lastClickedIdRef = useRef<string | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [localBrowser, setLocalBrowser] = useState(() => localStorage.getItem("ihub:localBrowser") === "true");
  // Local runs only exist in dev; a stale localStorage flag must never flip a prod run off Cloud Run
  const effectiveLocalBrowser = import.meta.env.DEV && localBrowser;

  if (!flowDbId) {
    supabase
      .from("flows")
      .select("id")
      .eq("slug", flowId)
      .single()
      .then(({ data }) => { if (data) setFlowDbId(data.id); });
  }

  const qProfiles = useQ_PageReadiness_Profiles({
    organizationId,
    flowId: flowDbId ?? "",
  });

  const profileIds = useMemo(() => qProfiles.profiles.map((p) => p.id), [qProfiles.profiles]);

  const qResults = useQ_PageReadiness_ProfileResults({
    organizationId,
    flowDbId: flowDbId ?? "",
    profileIds,
  });

  const mProfileCreate = useM_PageReadiness_ProfileCreate({
    organizationId,
    flowId: flowDbId ?? "",
  });

  const mProfileDelete = useM_PageReadiness_ProfileDelete({
    organizationId,
    flowId: flowDbId ?? "",
  });

  const mTestRun = useM_PageReadiness_TestRun({
    organizationId,
    flowSlug: flowId,
    flowDbId: flowDbId ?? "",
  });

  const qProviderConfig = useQ_PageSettings_ProviderConfig({
    organizationId,
    flowSlug: flowId,
  });

  const enabledStates = useMemo(
    () => new Set(qProviderConfig.config?.enabled_states ?? []),
    [qProviderConfig.config?.enabled_states],
  );

  // Flat id list in render order (healthy first, then expected failures, per state) — anchors shift-click ranges
  const orderedIds = useMemo(
    () => qProfiles.stateKeys.flatMap((state) => {
      const list = qProfiles.profilesByState[state] ?? [];
      return [...list.filter((p) => !p.expected_failure), ...list.filter((p) => p.expected_failure)].map((p) => p.id);
    }),
    [qProfiles.stateKeys, qProfiles.profilesByState],
  );

  const selectedProfiles = useMemo(
    () => qProfiles.profiles.filter((p) => selectedIds.has(p.id)),
    [qProfiles.profiles, selectedIds],
  );

  const handleSelect = useCallback((profileId: string, checked: boolean, shiftKey: boolean) => {
    // Capture the anchor before setState: the updater runs lazily (after the ref
    // is re-pointed at this click), so reading the ref inside it kills the range.
    const anchor = lastClickedIdRef.current;
    lastClickedIdRef.current = profileId;
    const a = anchor ? orderedIds.indexOf(anchor) : -1;
    const b = orderedIds.indexOf(profileId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const apply = (id: string) => (checked ? next.add(id) : next.delete(id));
      if (shiftKey && a !== -1 && b !== -1) {
        for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
          const id = orderedIds[i];
          if (id) apply(id);
        }
      } else {
        apply(profileId);
      }
      return next;
    });
  }, [orderedIds]);

  const handleSelectState = useCallback((state: string, checked: boolean) => {
    const list = qProfiles.profilesByState[state] ?? [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) list.filter((p) => !p.expected_failure).forEach((p) => next.add(p.id));
      else list.forEach((p) => next.delete(p.id));
      return next;
    });
  }, [qProfiles.profilesByState]);

  const handleAddProfile = async (values: SessionComposerValues) => {
    try {
      await mProfileCreate.mutation.mutateAsync(values);
      message.success("Profile created");
      setAddModalOpen(false);
      addForm.resetFields();
    } catch {
      message.error("Failed to create profile");
    }
  };

  const handleDeleteProfile = (profile: ProfileRow) => {
    modal.confirm({
      title: `Delete ${profile.key}?`,
      content: profile.label
        ? `"${profile.label}" will be permanently deleted.`
        : "This profile will be permanently deleted.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await mProfileDelete.mutation.mutateAsync({ profileId: profile.id });
          setSelectedIds((prev) => { const next = new Set(prev); next.delete(profile.id); return next; });
          message.success("Profile deleted");
        } catch {
          message.error("Failed to delete profile");
        }
      },
    });
  };

  const launchProfile = useCallback(
    (profile: ProfileRow) => mTestRun.mutation.mutateAsync({
      profileId: profile.id,
      ...profileToComposerValues(profile),
      ...(effectiveLocalBrowser ? { localBrowser: true } : {}),
    }),
    [mTestRun.mutation, effectiveLocalBrowser],
  );

  const handleRun = useCallback(async (profile: ProfileRow) => {
    setLaunchingIds((prev) => new Set(prev).add(profile.id));
    try {
      await launchProfile(profile);
      message.success(`Test started for ${profile.key}`);
    } catch {
      message.error(`Failed to start test for ${profile.key}`);
    } finally {
      setLaunchingIds((prev) => { const next = new Set(prev); next.delete(profile.id); return next; });
    }
  }, [launchProfile, message]);

  const handleRunSelected = useCallback(async () => {
    const targets = selectedProfiles.filter((p) => {
      const status = qResults.resultsByProfile[p.id]?.status;
      return status !== "running" && !launchingIds.has(p.id);
    });
    const skipped = selectedProfiles.length - targets.length;
    if (targets.length === 0) {
      message.info("All selected tests are already running");
      return;
    }
    setBulkRunning(true);
    setLaunchingIds((prev) => new Set([...prev, ...targets.map((t) => t.id)]));
    const results = await Promise.allSettled(targets.map((p) => launchProfile(p)));
    setLaunchingIds((prev) => { const next = new Set(prev); targets.forEach((t) => next.delete(t.id)); return next; });
    setBulkRunning(false);
    const failed = results.filter((r) => r.status === "rejected").length;
    const launched = targets.length - failed;
    if (launched > 0) message.success(`Launched ${launched} test${launched === 1 ? "" : "s"}${skipped > 0 ? ` (${skipped} already running)` : ""}`);
    if (failed > 0) message.error(`${failed} test${failed === 1 ? "" : "s"} failed to launch`);
    setSelectedIds(new Set());
  }, [selectedProfiles, qResults.resultsByProfile, launchingIds, launchProfile, message]);

  const collapseItems = qProfiles.stateKeys.map((state) => {
    const stateProfiles = qProfiles.profilesByState[state] ?? [];
    const healthy = stateProfiles.filter((p) => !p.expected_failure);
    const flagged = stateProfiles.filter((p) => p.expected_failure);
    const isDisabled = enabledStates.size > 0 && !enabledStates.has(state);
    const allHealthySelected = healthy.length > 0 && healthy.every((p) => selectedIds.has(p.id));
    const someSelected = stateProfiles.some((p) => selectedIds.has(p.id));

    const renderProfileRow = (profile: ProfileRow) => (
      <ProfileRowItem
        key={profile.id}
        profile={profile}
        result={qResults.resultsByProfile[profile.id] ?? null}
        isLaunching={launchingIds.has(profile.id)}
        selected={selectedIds.has(profile.id)}
        onSelect={(checked, shiftKey) => handleSelect(profile.id, checked, shiftKey)}
        organizationId={organizationId}
        flowId={flowId}
        flowDbId={flowDbId ?? ""}
        onEdit={() => {
          setEditProfile(profile);
          editForm.setFieldsValue(
            flattenToFormValues({
              ...profileToComposerValues(profile),
              label: profile.label ?? undefined,
              expectedFailure: profile.expected_failure,
            }),
          );
        }}
        onDelete={() => handleDeleteProfile(profile)}
        onRun={() => handleRun(profile)}
      />
    );

    return {
      key: state,
      label: (
        <Flex align="center" gap={token.marginXS}>
          <Checkbox
            checked={allHealthySelected}
            indeterminate={someSelected && !allHealthySelected}
            disabled={isDisabled || stateProfiles.length === 0}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleSelectState(state, e.target.checked)}
          />
          <Typography.Text strong style={isDisabled ? { opacity: 0.45 } : undefined}>{state}</Typography.Text>
          <Tag>{healthy.length}</Tag>
          {flagged.length > 0 && (
            <Tooltip title="Expected failures — excluded from select-all">
              <Tag color="warning">{flagged.length} expected</Tag>
            </Tooltip>
          )}
          {isDisabled && (
            <Tooltip title="This state is not enabled in provider settings">
              <Tag color="default">disabled</Tag>
            </Tooltip>
          )}
        </Flex>
      ),
      children: (
        <Flex vertical gap={token.marginXS}>
          {healthy.map(renderProfileRow)}
          {flagged.length > 0 && (
            <Flex
              vertical
              gap={token.marginXS}
              style={{
                background: token.colorWarningBg,
                borderRadius: token.borderRadiusSM,
                padding: token.paddingXS,
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                Expected failures — known to crash, excluded from select-all
              </Typography.Text>
              {flagged.map(renderProfileRow)}
            </Flex>
          )}
        </Flex>
      ),
    };
  });

  const counts = useMemo(() => {
    const c = { passed: 0, failed: 0, running: 0, notRun: 0, expectedFailed: 0 };
    for (const p of qProfiles.profiles) {
      const result = qResults.resultsByProfile[p.id];
      if (!result) { c.notRun++; continue; }
      if (result.status === "running") c.running++;
      else if (result.status === "passed") c.passed++;
      else if (p.expected_failure) c.expectedFailed++;
      else c.failed++;
    }
    return c;
  }, [qProfiles.profiles, qResults.resultsByProfile]);

  return (
    <div>
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap={token.marginSM}
        style={{
          position: "sticky",
          top: 48,
          zIndex: 10,
          background: token.colorBgLayout,
          paddingBottom: token.marginMD,
          marginBottom: token.marginXS,
        }}
      >
        <Flex align="center" wrap="wrap" gap={token.marginMD}>
          <Typography.Title level={4} style={{ margin: 0 }}>Readiness</Typography.Title>
          <Tag>{qProfiles.profiles.length} profiles</Tag>
          {counts.passed > 0 && <Tag color="success">{counts.passed} passed</Tag>}
          {counts.failed > 0 && <Tag color="error">{counts.failed} failed</Tag>}
          {counts.expectedFailed > 0 && (
            <Tooltip title="Failures on profiles marked as expected failures">
              <Tag color="warning">{counts.expectedFailed} expected-fail</Tag>
            </Tooltip>
          )}
          {counts.running > 0 && <Tag color="processing">{counts.running} running</Tag>}
          {counts.notRun > 0 && <Tag color="default">{counts.notRun} not run</Tag>}
        </Flex>
        <Flex align="center" wrap="wrap" gap={token.marginSM}>
          {import.meta.env.DEV && (
            <Flex align="center" gap={token.marginSM}>
              <Switch
                checked={localBrowser}
                onChange={(checked) => {
                  setLocalBrowser(checked);
                  localStorage.setItem("ihub:localBrowser", String(checked));
                }}
                checkedChildren={<LaptopOutlined />}
                unCheckedChildren={<LaptopOutlined />}
                size="small"
              />
              <Typography.Text type={localBrowser ? "warning" : "secondary"} style={{ fontSize: token.fontSizeSM }}>
                {localBrowser ? "Local" : "Cloud Run"}
              </Typography.Text>
            </Flex>
          )}
          <Button
            icon={<PlayCircleOutlined />}
            onClick={handleRunSelected}
            disabled={selectedProfiles.length === 0 || bulkRunning}
            loading={bulkRunning}
          >
            Run selected{selectedProfiles.length > 0 ? ` (${selectedProfiles.length})` : ""}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            Add Profile
          </Button>
        </Flex>
      </Flex>

      {qProfiles.profiles.length === 0 ? (
        <Empty description="No test profiles yet. Add one to get started." />
      ) : (
        <Collapse
          items={collapseItems}
          size="small"
        />
      )}

      <Modal
        title="Add Test Profile"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }}
        onOk={() => addForm.submit()}
        confirmLoading={mProfileCreate.mutation.isPending}
        width={640}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        destroyOnHidden
      >
        <App_SessionComposer composerMode="profile" form={addForm} onFinish={handleAddProfile} />
      </Modal>

      <Modal
        title={`Edit ${editProfile?.key ?? "Profile"}`}
        open={!!editProfile}
        onCancel={() => { setEditProfile(null); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        width={640}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        destroyOnHidden
      >
        {editProfile && (
          <EditProfileForm
            profile={editProfile}
            organizationId={organizationId}
            flowId={flowDbId ?? ""}
            form={editForm}
            onSuccess={() => { setEditProfile(null); editForm.resetFields(); message.success("Profile updated"); }}
            onError={() => message.error("Failed to update profile")}
          />
        )}
      </Modal>
    </div>
  );
}

function fmtDur(ms: number): string {
  const d = dayjs.duration(ms);
  const mins = Math.floor(d.asMinutes());
  const secs = d.seconds();
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function ProfileRowItem({
  profile,
  result,
  isLaunching,
  selected,
  onSelect,
  organizationId,
  flowId,
  onEdit,
  onDelete,
  onRun,
}: {
  profile: ProfileRow;
  result: ProfileResult | null;
  isLaunching: boolean;
  selected: boolean;
  onSelect: (checked: boolean, shiftKey: boolean) => void;
  organizationId: string;
  flowId: string;
  flowDbId: string;
  onEdit: () => void;
  onDelete: () => void;
  onRun: () => void;
}) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const displayName = profile.label || `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || profile.key;

  const status = result?.status ?? "not_run";
  const statusMeta = STATUS_TAG[status] ?? { color: "default", label: status };
  const isRunning = status === "running";
  const isBusy = isRunning || isLaunching;

  const handleResultClick = () => {
    if (result?.sessionId) {
      navigate({
        to: "/$organizationId/$flowId/sessions/$sessionId",
        params: { organizationId, flowId, sessionId: result.sessionId },
      });
    }
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      wrap="wrap"
      gap={token.marginXS}
      style={{
        padding: `${token.paddingXS}px ${token.paddingSM}px`,
        borderRadius: token.borderRadiusSM,
        background: token.colorBgTextHover,
      }}
    >
      <Flex align="center" wrap="wrap" gap={token.marginSM}>
        {/* The checkbox is display-only (pointer-events none); this span owns the raw
            mouse event so shiftKey survives — AntD's label→input change re-dispatch drops it. */}
        <span
          role="checkbox"
          aria-checked={selected}
          data-profile-select={profile.id}
          style={{ display: "inline-flex", cursor: "pointer" }}
          onMouseDown={(e) => {
            // Shift-click should extend the selection, not highlight page text
            if (e.shiftKey) e.preventDefault();
          }}
          onClick={(e) => onSelect(!selected, e.shiftKey)}
        >
          <Checkbox checked={selected} tabIndex={-1} style={{ pointerEvents: "none" }} />
        </span>
        <Typography.Text code style={{ fontSize: token.fontSizeSM }}>{profile.key}</Typography.Text>
        <Typography.Text>{displayName}</Typography.Text>
        {isLaunching ? (
          <Tag color="processing">Launching...</Tag>
        ) : (
          <Tag
            color={statusMeta.color}
            style={result ? { cursor: "pointer" } : undefined}
            onClick={result ? handleResultClick : undefined}
          >
            {statusMeta.label}
          </Tag>
        )}
        {result && !isLaunching && (() => {
          const created = dayjs(result.createdAt);
          const ended = result.endedAt ? dayjs(result.endedAt) : null;
          return (
            <>
              {isRunning && (
                <Tag color="processing" style={{ fontSize: token.fontSizeSM }}>
                  {fmtDur(dayjs().diff(created))}
                </Tag>
              )}
              {ended && result.executionMs != null ? (
                <Tooltip title={`Total: ${fmtDur(ended.diff(created))}`}>
                  <Tag color="green" style={{ fontSize: token.fontSizeSM }}>exec {fmtDur(result.executionMs)}</Tag>
                </Tooltip>
              ) : ended ? (
                <Tag color="blue" style={{ fontSize: token.fontSizeSM }}>
                  {fmtDur(ended.diff(created))}
                </Tag>
              ) : null}
              {result.policyNumber && (
                <Tag color="cyan" style={{ fontSize: token.fontSizeSM }}>{result.policyNumber}</Tag>
              )}
              {status === "failed" && (
                <Tooltip title={result.endMessage}>
                  <Typography.Text type="danger" style={{ fontSize: token.fontSizeSM }}>
                    {result.endCode}
                  </Typography.Text>
                </Tooltip>
              )}
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {created.fromNow()}
              </Typography.Text>
            </>
          );
        })()}
      </Flex>
      <Flex gap={token.marginXS}>
        <Button
          type="text"
          size="small"
          icon={<PlayCircleOutlined />}
          onClick={onRun}
          disabled={isBusy}
          loading={isBusy}
        >
          Run
        </Button>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
      </Flex>
    </Flex>
  );
}

function EditProfileForm({
  profile,
  organizationId,
  flowId,
  form,
  onSuccess,
  onError,
}: {
  profile: ProfileRow;
  organizationId: string;
  flowId: string;
  form: ReturnType<typeof Form.useForm>[0];
  onSuccess: () => void;
  onError: () => void;
}) {
  const mProfileUpdate = useM_PageReadiness_ProfileUpdate({
    organizationId,
    flowId,
    profileId: profile.id,
  });

  const handleFinish = async (values: SessionComposerValues) => {
    try {
      await mProfileUpdate.mutation.mutateAsync(values);
      onSuccess();
    } catch {
      onError();
    }
  };

  return <App_SessionComposer composerMode="profile" form={form} onFinish={handleFinish} />;
}
